/**
 * GLDN Cart Thumb Guard
 *
 * Some external script (likely an app — Easify / BOGOS / Shopify's
 * line_item.image fallback behavior) swaps the src attribute of cart
 * line-item thumbnails to the Preview uploaded file whenever a line item
 * has a "Preview" property. Visually, the user sees the product photo
 * render correctly, then flip to the personalizer preview thumbnail.
 *
 * We can't find the offending script by name. This guard just watches
 * for src changes on .cart-items__media-image and restores the
 * canonical URL captured server-side via data-gldn-canonical-src.
 * The MutationObserver fires for every attribute change, so the swap
 * reverts instantly and invisibly.
 *
 * Why we read data-gldn-canonical-src fresh on every mutation instead
 * of caching it: in the cart drawer (which persists across navigations
 * and morphs in place on every cart update), Horizon's morph reuses the
 * same <img> element for a new line item, rewriting both src and
 * data-gldn-canonical-src to the new product's values. A cached
 * canonical would still hold the previous product's URL and the
 * observer would revert src to the *old* image — producing an off-by-one
 * shift where each row displays the previous occupant's image.
 *
 * Logs a one-time console warning with the offending URL the first
 * time a revert happens so we can optionally track down the culprit.
 */
(function () {
  var loggedOnce = false;

  function guardImage(img) {
    if (!img.getAttribute('data-gldn-canonical-src')) return;
    if (img._gldnThumbGuard) return;

    // If src already drifted at first guard call (initial render race), fix it
    var initialCanonical = img.getAttribute('data-gldn-canonical-src');
    if (img.getAttribute('src') !== initialCanonical) {
      img.setAttribute('src', initialCanonical);
      if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
    }

    var observer = new MutationObserver(function (mutations) {
      var canonical = img.getAttribute('data-gldn-canonical-src');
      if (!canonical) return;
      mutations.forEach(function (m) {
        if (m.attributeName === 'src' && img.getAttribute('src') !== canonical) {
          if (!loggedOnce) {
            console.warn('[gldn-cart-thumb-guard] Third-party script tried to swap cart thumbnail src to:', img.getAttribute('src'), '- reverting to canonical product image. This warning logs once per page load.');
            loggedOnce = true;
          }
          img.setAttribute('src', canonical);
        }
        if (m.attributeName === 'srcset' && img.hasAttribute('srcset')) {
          img.removeAttribute('srcset');
        }
      });
    });
    observer.observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });
    img._gldnThumbGuard = true;
  }

  function guardAll() {
    document.querySelectorAll('.cart-items__media-image[data-gldn-canonical-src]').forEach(guardImage);
  }

  // Guard any cart thumbs present at load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', guardAll);
  } else {
    guardAll();
  }

  // Re-guard after cart section re-renders (addToCartSuccess swap,
  // Horizon's morphSection, cart-drawer open, etc.).
  document.addEventListener('cart:update', function () {
    // New DOM nodes may take a tick to settle
    requestAnimationFrame(guardAll);
    setTimeout(guardAll, 100);
  });

  // Also expose for explicit calls from other scripts.
  window.gldnCartThumbGuard = guardAll;

  // ---- Price guard: strip trailing .00 from .cart-items__price values ----
  // Horizon (or a currency app) sometimes re-renders line prices in the
  // default money format ($448.00) after our server-side
  // money_without_trailing_zeros ($448) initial render. Watch for text
  // mutations on .cart-items__price and its text-component child and
  // rewrite ".00" to empty on the fly.
  function stripTrailingZerosIn(el) {
    if (!el) return;
    el.childNodes.forEach(function (node) {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue && node.nodeValue.indexOf('.00') !== -1) {
        node.nodeValue = node.nodeValue.replace(/\.00\b/g, '');
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        stripTrailingZerosIn(node);
      }
    });
  }

  function guardPrice(cell) {
    if (cell._gldnPriceGuard) return;
    stripTrailingZerosIn(cell);

    // Also normalize text-component value attribute
    cell.querySelectorAll('text-component[value*=".00"]').forEach(function (tc) {
      tc.setAttribute('value', tc.getAttribute('value').replace(/\.00\b/g, ''));
    });

    var observer = new MutationObserver(function () {
      stripTrailingZerosIn(cell);
      cell.querySelectorAll('text-component[value*=".00"]').forEach(function (tc) {
        tc.setAttribute('value', tc.getAttribute('value').replace(/\.00\b/g, ''));
      });
    });
    observer.observe(cell, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['value'] });
    cell._gldnPriceGuard = true;
  }

  function guardAllPrices() {
    document.querySelectorAll('.cart-items__price, .cart-items__unit-price-wrapper').forEach(guardPrice);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', guardAllPrices);
  } else {
    guardAllPrices();
  }
  document.addEventListener('cart:update', function () {
    requestAnimationFrame(guardAllPrices);
    setTimeout(guardAllPrices, 100);
    setTimeout(guardAllPrices, 500);
  });
  window.gldnCartPriceGuard = guardAllPrices;
})();
